/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	useMutation,
	useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import { notifications } from '@mantine/notifications';

// Utils
import {
	convertContentToJSON,
	exportCSV,
} from '@utils/functions';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

type TFilter = {
	projects?: string[];
	sites?: string[];
	verified?: boolean[];
	status?: string[];
};

type TResponse = {
	status?: string;
	content: string;
	verified?: boolean[];
};

export const download = (
	data: any,
	queryParams: any
): Promise<{
	response: any;
}> => {
	let filters: TFilter = {};
	const { project, site, verified } = queryParams;

	if (
		project &&
		Array.isArray(project) &&
		project.length > 0
	) {
		filters.projects = project;
	}

	if (site && Array.isArray(site) && site.length > 0) {
		filters.sites = site;
	}

	if (Array.isArray(verified) && verified.length === 1) {
		filters.verified = verified[0];
	}

	if (
		data.productions &&
		Array.isArray(data.productions) &&
		data.productions.length > 0
	) {
		filters = {};
	}

	return apiClient.post('/exports/productions', {
		...data,
		...filters,
	});
};

export const getExports = (
	id: string
): Promise<TResponse> => {
	return apiClient.get(`/exports/${id}`);
};

export const useDownload = (
	page: number,
	size: number,
	queryParams: any
) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { mutate, isPending: isLoading } = useMutation({
		mutationKey: ['download-production-csv'],
		mutationFn: (_body) => download(_body, queryParams),

		onSuccess: async (data: any) => {
			const response: any = await getExports(data._id);

			const idNotification = notifications.show({
				loading: true,
				title: t('global.downloadInProgress'),
				message: t(
					'productions.notifications.productionDownloadCsvInProgress'
				),
				autoClose: false,
				withCloseButton: false,
			});

			if (response.status == 'IN_PROGRESS') {
				const delay = (ms: number) =>
					new Promise((resolve) =>
						setTimeout(resolve, ms)
					);

				const pollStatus = async (count = 0) => {
					if (count > 5) {
						notifications.update({
							id: idNotification,
							loading: false,
							color: 'error',
							title: t('global.error'),
							message: t(
								'productions.notifications.productionDownloadCsvStillInProgress'
							),
							autoClose: 7000,
						});
						return;
					}

					try {
						count++;

						const response: TResponse = await getExports(
							data._id
						);

						if (response.status !== 'IN_PROGRESS') {
							if (response.status === 'FAILED') {
								notifications.show({
									title: t('global.error'),
									message: t('errors.sorryMessage'),
									color: 'error',
								});
								return;
							} else {
								const jsonContent = convertContentToJSON(
									response.content
								);
								const fileName = `Inuk_Extract_Productions_${dayjs().format(
									'YYYY-MM-DD--HH-mm-ss'
								)}`;
								exportCSV(jsonContent, fileName);

								notifications.update({
									id: idNotification,
									loading: false,
									color: 'success',
									title: t('global.success'),
									message: t(
										'productions.notifications.productionSuccessfullyDownloaded'
									),

									autoClose: 7000,
								});

								queryClient.invalidateQueries({
									queryKey: [
										'productions',
										{ page, size },
									],
								});
								return;
							}
						}

						await delay(5000);
						await pollStatus(count);
					} catch (error) {
						console.error('Error:', error);
						notifications.update({
							id: idNotification,
							loading: false,
							color: 'error',
							title: t('global.error'),
							message: JSON.stringify(error),

							autoClose: 7000,
						});
						return;
					}
				};

				pollStatus();
			} else {
				try {
					const jsonContent: any = convertContentToJSON(
						response.content
					);
					const fileName = `Inuk_Extract_Productions_${dayjs().format(
						'YYYY-MM-DD--hh-mm-ss'
					)}`;
					exportCSV(jsonContent, fileName);
				} catch (error: any) {
					console.log('Here error :', error);
					notifications.update({
						id: idNotification,
						loading: false,
						color: 'error',
						title: t('global.error'),
						message: t('errors.sorryMessage'),
						autoClose: 7000,
					});
				}
				if (response.status == 'FAILED') {
					notifications.update({
						id: idNotification,
						loading: false,
						color: 'error',
						title: t('global.error'),
						message: t('global.error'),

						autoClose: 7000,
					});
				} else {
					notifications.update({
						id: idNotification,
						loading: false,
						color: 'success',
						title: t('global.success'),
						message: t(
							'productions.notifications.productionSuccessfullyDownloaded'
						),

						autoClose: 7000,
					});
				}
				queryClient.invalidateQueries({
					queryKey: [
						'productions',
						{ page: page, size: size },
					],
				});
			}
		},
		onError: (error: any) => {
			notifications.show({
				title: t('global.error'),
				message: t(`api.${error.type}`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading };
};
